import { Dispatch, SetStateAction } from 'react';
import * as R from 'ramda';
import { MdClose, MdEditNote } from 'react-icons/md';
import { PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design } from '@Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { EditsMap, ExtendEditModalData } from '../linear-buying-utils';
import { PendingEdit } from './PendingEdit';
import { PendingNewRow } from './PendingNewRow';
import './PendingChanges.scss';

/** One plan line within a network, holding everything pending against it. */
interface ChangeLine {
  id: string;
  label: string;
  edits: ExtendEditModalData[];
  /** Each entry is one new-row definition, spread across however many weeks it was added to. */
  newRowGroups: PlanRow[][];
}

interface NetworkChanges {
  network: string;
  lines: ChangeLine[];
  changeCount: number;
}

/**
 * Creates a key to be able to compare exact copies of the same plan rows (including creatives).
 * This way, the same plan row across different weeks can have the same key to group together in Pending Changes.
 */
const makeEntireRowKey = (row: PlanRow): string => {
  const {
    avail,
    length,
    daypart_start,
    daypart_end,
    dow,
    type,
    count,
    cost,
    notes,
    network,
    creatives
  } = row;
  const creativesStr = JSON.stringify(creatives);

  return `${network}_${avail}_${daypart_start}_${daypart_end}_${dow}_${type}_${length}_${count}_${cost}_${
    notes || ''
  }_${creativesStr}`;
};

interface PendingChangesProps {
  editsMap: EditsMap;
  newRows: Record<string, PlanRow>;
  setEditsMap: Dispatch<SetStateAction<EditsMap>>;
  setNewRows: Dispatch<SetStateAction<Record<string, PlanRow>>>;
  setExtendEditModalData: Dispatch<SetStateAction<ExtendEditModalData | undefined>>;
  setShowPendingChanges: Dispatch<SetStateAction<boolean>>;
}

export const PendingChanges = ({
  editsMap,
  newRows,
  setEditsMap,
  setNewRows,
  setExtendEditModalData,
  setShowPendingChanges
}: PendingChangesProps) => {
  const changesByNetwork = groupChangesByNetwork(editsMap, newRows);

  const deleteEdit = (key: string, editType: string) => {
    // Create a new row that has the edit removed.
    const removedEdit: PlanRow = {
      ...editsMap[key],
      edits: R.omit([editType], editsMap[key].edits)
    };

    // If after removing the edit, the "edits" field is empty, delete the whole row from editsMap
    // because there are no edits left for this row.
    if (R.isEmpty(removedEdit.edits)) {
      setEditsMap(current => R.omit([key], current));
    } else {
      // Otherwise, there are still other edits remaining for this row so just update editsMap with
      // the removedEdit row.
      setEditsMap(current => ({ ...current, [key]: removedEdit }));
    }
  };

  const deleteNewRow = (key: string) => {
    setNewRows(current => R.omit([key], current));
  };

  return (
    <div className='pending-changes-container'>
      <div className='pending-changes-top'>
        <span className='panel-title'>Pending Changes</span>
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          onClick={() => setShowPendingChanges(false)}
        >
          <MdClose />
        </IconButton>
      </div>
      <div className='pending-changes-body'>
        {changesByNetwork.length === 0 && <EmptyState />}
        {changesByNetwork.map(networkChanges => (
          <NetworkGroup
            key={networkChanges.network}
            networkChanges={networkChanges}
            deleteEdit={deleteEdit}
            deleteNewRow={deleteNewRow}
            setExtendEditModalData={setExtendEditModalData}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Collects every pending change under the network it belongs to, then under the plan line within
 * that network.
 *
 * Lines are keyed off the row's current values rather than its edits: changing a rotation or a
 * length is a change *to* a line, so the line it files under is the one that exists today.
 */
const groupChangesByNetwork = (
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>
): NetworkChanges[] => {
  const linesByNetwork: Record<string, Record<string, ChangeLine>> = {};

  const getLine = (row: PlanRow): ChangeLine => {
    const { network, rotation, length, avail, daypart_start, daypart_end, dow, type } = row;
    // Keyed on the whole line identity, not just the parts the label shows, so that two lines
    // differing only by daypart don't merge into one.
    const id = `${rotation}_${length}_${avail}_${daypart_start}_${daypart_end}_${dow}_${type}`;

    if (!linesByNetwork[network]) {
      linesByNetwork[network] = {};
    }
    if (!linesByNetwork[network][id]) {
      linesByNetwork[network][id] = {
        id,
        label: `${rotation} · ${length}s · ${avail}`,
        edits: [],
        newRowGroups: []
      };
    }
    return linesByNetwork[network][id];
  };

  for (const row of R.values(editsMap)) {
    getLine(row).edits.push({ row, edits: row.edits!, selectedEditToExtend: null });
  }

  // New rows identical apart from their week collapse into one card listing those weeks, so they
  // are gathered by full-row identity before being attached to a line.
  const newRowsByDefinition: Record<string, PlanRow[]> = {};
  for (const row of R.values(newRows)) {
    const definition = makeEntireRowKey(row);
    newRowsByDefinition[definition] = [...(newRowsByDefinition[definition] || []), row];
  }
  for (const rows of Object.values(newRowsByDefinition)) {
    getLine(rows[0]).newRowGroups.push(rows);
  }

  return Object.keys(linesByNetwork)
    .sort()
    .map(network => {
      const lines = Object.values(linesByNetwork[network]).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      for (const line of lines) {
        // Weeks are ISO dates, so they sort chronologically as strings.
        line.edits.sort((a, b) => a.row.week.localeCompare(b.row.week));
      }

      return {
        network,
        lines,
        changeCount: lines.reduce(
          (total, line) =>
            total +
            line.edits.length +
            line.newRowGroups.reduce((rowCount, rows) => rowCount + rows.length, 0),
          0
        )
      };
    });
};

interface NetworkGroupProps {
  networkChanges: NetworkChanges;
  deleteEdit: (key: string, editType: string) => void;
  deleteNewRow: (key: string) => void;
  setExtendEditModalData: Dispatch<SetStateAction<ExtendEditModalData | undefined>>;
}

/** Every pending change against one network, split into its plan lines. */
const NetworkGroup = ({
  networkChanges: { network, lines, changeCount },
  deleteEdit,
  deleteNewRow,
  setExtendEditModalData
}: NetworkGroupProps) => (
  <div className='change-group'>
    <div className='change-group-header'>
      <span className='change-group-label'>{network}</span>
      <span className='change-group-count'>
        {conditionallyPluralizeWithCount(changeCount, 'change')}
      </span>
    </div>
    {lines.map(line => (
      <div className='change-line' key={line.id}>
        <div className='change-line-label'>{line.label}</div>
        <div className='change-line-list'>
          {line.edits.map(editData => (
            <PendingEdit
              key={editData.row.key}
              editData={editData}
              deleteEdit={deleteEdit}
              setExtendEditModalData={setExtendEditModalData}
            />
          ))}
          {line.newRowGroups.map(rows => (
            <PendingNewRow
              key={makeEntireRowKey(rows[0])}
              rows={rows}
              deleteNewRow={deleteNewRow}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className='empty-state'>
    <MdEditNote />
    <div className='empty-state-title'>No pending changes</div>
    <div className='empty-state-detail'>
      Edits and new rows collect here until you save or discard them.
    </div>
  </div>
);
